<template>
    <div class="assistant-panel">
        <div v-if="showHistory" class="history-overlay">
            <div class="history-head">
                <span class="history-title">Sohbet Geçmişi</span>
                <button type="button" class="history-back" @click="showHistory = false"
                        title="Kapat" aria-label="Kapat">×</button>
            </div>
            <div class="history-body">
                <div v-if="historyLoading" class="history-state">Yükleniyor…</div>
                <div v-else-if="historyError" class="history-state">{{ historyError }}</div>
                <div v-else-if="!historyList.length" class="history-state">Henüz sohbet geçmişin yok.</div>
                <ul v-else class="history-list">
                    <li v-for="c in historyList" :key="c.sessionId">
                        <button type="button" class="history-item" @click="openConversation(c.sessionId)">
                            <span class="history-item-title">{{ c.title }}</span>
                            <span class="history-item-meta">{{ formatHistoryDate(c.lastMessageAtUtc) }} · {{ c.turnCount }} mesaj</span>
                        </button>
                    </li>
                </ul>
            </div>
        </div>

        <assistant-quick-questions-editor
            v-if="showQuickQuestionEditor"
            :scope="scope"
            :context-label="quickQuestionContextLabel"
            :questions="activeSuggestions"
            :is-customized="hasPersonalQuickQuestions"
            :max-count="quickQuestionMaxCount"
            :max-length="quickQuestionMaxLength"
            @close="showQuickQuestionEditor = false"
            @saved="onQuickQuestionsSaved"
            @reset="onQuickQuestionsReset" />

        <!-- Mesaj alanı: scroll'lu, conversation thread -->
        <div class="messages-area" ref="messagesArea">
            <!-- Empty state -->
            <div v-if="isEmpty" class="empty-state">
                <div class="empty-avatar" aria-hidden="true">
                    <span>✨</span>
                </div>
                <p class="empty-greeting">
                    <template v-if="customerName">
                        <strong>{{ customerName }}</strong> danışan sayfasındasınız. Nasıl yardımcı olabilirim?
                    </template>
                    <template v-else>
                        Nasıl yardımcı olabilirim?
                    </template>
                </p>
                <div v-if="supportsQuickQuestions" class="suggestion-head">
                    <span>{{ $t('AssistantPanel.QuickQuestionsTitle') }}</span>
                    <button type="button" @click="openQuickQuestionEditor">
                        <span aria-hidden="true">✎</span> {{ $t('Edit') }}
                    </button>
                </div>
                <div class="suggestion-grid" :class="{ 'suggestion-grid--headed': supportsQuickQuestions }">
                    <button v-for="s in activeSuggestions" :key="s"
                            type="button" class="suggestion-chip"
                            @click="askSuggestion(s)">{{ s }}</button>
                </div>
            </div>

            <!-- Conversation thread -->
            <template v-else>
                <div v-for="(msg, i) in messages" :key="i"
                     :class="['message', 'message-' + msg.role]">
                    <div v-if="msg.role === 'assistant'" class="msg-avatar" aria-hidden="true">
                        <span>✨</span>
                    </div>
                    <div class="msg-content">
                        <div v-if="msg.role === 'assistant'" class="msg-name">
                            Vega AI <span class="msg-name-spark" aria-hidden="true">✨</span>
                        </div>
                        <div v-if="msg.role === 'assistant'"
                             class="msg-bubble stella-md"
                             v-dompurify-html="renderMarkdown(msg.content)"></div>
                        <div v-else class="msg-bubble">{{ msg.content }}</div>
                        <div v-if="msg.role === 'assistant'" class="msg-actions">
                            <div v-if="isInviteSlot(msg, i) && msg.feedback.inviteReady"
                                 class="fb-invite">
                                <span class="fb-invite-q">{{ $t('AssistantPanel.FeedbackInvite') }}</span>
                                <div class="fb-invite-actions">
                                    <button type="button" class="fb-choice"
                                            :disabled="msg.feedback.saving"
                                            @click="toggleLike(msg)"
                                            :title="$t('AssistantPanel.FeedbackYesTitle')"
                                            :aria-label="$t('AssistantPanel.FeedbackYesTitle')">
                                        <span aria-hidden="true">👍</span><span>{{ $t('Yes') }}</span>
                                    </button>
                                    <button type="button" class="fb-choice"
                                            :disabled="msg.feedback.saving"
                                            @click="toggleDislike(msg)"
                                            :title="$t('AssistantPanel.FeedbackNoTitle')"
                                            :aria-label="$t('AssistantPanel.FeedbackNoTitle')">
                                        <span aria-hidden="true">👎</span><span>{{ $t('No') }}</span>
                                    </button>
                                </div>
                            </div>

                            <div v-else class="fb-compact">
                                <button type="button"
                                        class="fb-icon-btn fb-copy"
                                        :class="{ 'is-copied': copiedIndex === i }"
                                        @click="copyMessage(msg.content, i)"
                                        :title="$t('Copy')" :aria-label="$t('Copy')">
                                    <svg v-if="copiedIndex === i" width="16" height="16" viewBox="0 0 24 24"
                                         fill="none" stroke="currentColor" stroke-width="2.5"
                                         stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                        <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                    <svg v-else width="16" height="16" viewBox="0 0 24 24"
                                         fill="none" stroke="currentColor" stroke-width="2"
                                         stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                    </svg>
                                </button>
                                <template v-if="msg.aiCallLogId && msg.feedback
                                                && !(isInviteSlot(msg, i) && !msg.feedback.inviteReady)">
                                    <span class="fb-sep" aria-hidden="true"></span>
                                    <button type="button"
                                            class="fb-icon-btn fb-thumb"
                                            :class="{ 'is-up': msg.feedback.rating === 'up' }"
                                            :disabled="msg.feedback.saving"
                                            @click="toggleLike(msg)"
                                            :title="$t('AssistantPanel.Like')" :aria-label="$t('AssistantPanel.Like')">👍</button>
                                    <button type="button"
                                            class="fb-icon-btn fb-thumb"
                                            :class="{ 'is-down': msg.feedback.rating === 'down' }"
                                            :disabled="msg.feedback.saving"
                                            @click="toggleDislike(msg)"
                                            :title="$t('AssistantPanel.Dislike')" :aria-label="$t('AssistantPanel.Dislike')">👎</button>
                                </template>
                            </div>
                        </div>

                        <div v-if="msg.role === 'assistant' && msg.feedback
                                   && msg.feedback.rating === 'down' && msg.feedback.chipsOpen"
                             class="fb-reasons">
                            <span class="fb-reasons-label">{{ $t('AssistantPanel.FeedbackReasonsLabel') }} <small>({{ $t('AssistantPanel.Optional') }})</small></span>
                            <button v-for="r in feedbackReasons" :key="r"
                                    type="button" class="fb-chip"
                                    :class="{ 'is-selected': msg.feedback.reason === r }"
                                    :disabled="msg.feedback.saving"
                                    @click="selectReason(msg, r)">{{ r }}</button>
                        </div>

                        <div v-if="msg.role === 'assistant' && msg.feedback && msg.feedback.commentOpen"
                             class="msg-feedback-box">
                            <textarea
                                class="msg-feedback-input"
                                v-model="msg.feedback.comment"
                                rows="2"
                                maxlength="2000"
                                :placeholder="$t('AssistantPanel.FeedbackCommentPlaceholder')"></textarea>
                            <div class="msg-feedback-actions">
                                <button type="button" class="fb-btn fb-btn--ghost"
                                        @click="cancelComment(msg)">{{ $t('Cancel') }}</button>
                                <button type="button" class="fb-btn fb-btn--primary"
                                        :disabled="msg.feedback.saving"
                                        @click="submitComment(msg)">{{ $t('Send') }}</button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Loading bubble (yeni cevap gelirken son turn'de görünür) -->
                <div v-if="loading" class="message message-assistant message-loading">
                    <div class="msg-avatar" aria-hidden="true"><span>✨</span></div>
                    <div class="msg-content">
                        <div class="msg-name">
                            Vega AI <span class="msg-name-spark" aria-hidden="true">✨</span>
                        </div>
                        <div class="msg-bubble msg-bubble--loading">
                            <span class="loading-dot"></span>
                            <span class="loading-dot"></span>
                            <span class="loading-dot"></span>
                            <!-- Tool döngüsü sürerken metin delta'sı GELMEZ; durum metni olmadan
                                 kullanıcı 2-6 saniye boş ekrana bakıyordu. -->
                            <span v-if="statusText" class="loading-status">{{ statusText }}</span>
                        </div>
                    </div>
                </div>

                <!-- Randevu onay kartı: tek randevu → tam kart, çoklu → tek toplu kart -->
                <template v-if="appointmentDrafts.length && !loading">
                    <appointment-confirm-card
                        v-if="appointmentDrafts.length === 1"
                        :draft="appointmentDrafts[0]"
                        @confirm="onAppointmentConfirmed(appointmentDrafts[0], $event)"
                        @cancel="onAppointmentDismissed(appointmentDrafts[0])"
                        @edit="onAppointmentEdit" />
                    <appointment-batch-card
                        v-else
                        :drafts="appointmentDrafts"
                        @confirmed-draft="onBatchDraftConfirmed"
                        @confirm="onBatchConfirmed"
                        @cancel="onBatchDismissed" />
                </template>

                <!-- Not onay kartı: taslağı kullanıcı onaylayınca /CustomerNote/InsertMany çağrılır -->
                <template v-if="noteDrafts.length && !loading">
                    <note-confirm-card
                        v-for="(nd, ndIdx) in noteDrafts"
                        :key="'note-draft-' + ndIdx"
                        :draft="nd"
                        :ai-call-log-id="lastAiCallLogId"
                        @confirm="onNoteConfirmed(nd, $event)"
                        @cancel="onNoteDismissed(nd)"
                        @edit="onNoteEdit" />
                </template>

                <!-- Başarı / hata rozetleri -->
                <div v-if="successMsg && !loading" class="assistant-success">
                    <span class="success-icon" aria-hidden="true">✓</span>
                    <span>{{ successMsg }}</span>
                </div>
                <div v-if="errorMsg && !loading" class="assistant-error">
                    <span class="error-icon" aria-hidden="true">!</span>
                    <span>{{ errorMsg }}</span>
                </div>
                <div v-if="creditsExhausted && !loading" class="assistant-notice">
                    <span class="notice-spark" aria-hidden="true">✨</span>
                    <span class="notice-text">{{ $t('AssistantPanel.CreditsExhausted') }}</span>
                </div>
            </template>
        </div>

        <!-- Footer: bağlam bar + input tek bordered grup -->
        <div class="panel-footer">
            <div class="footer-card">
                <!-- Bağlam route'a aittir; panel içinden başka veri kapsamına geçilemez. -->
                <div v-if="customerName" class="context-bar">
                    <span class="context-label">{{ $t('AssistantPanel.Context') }}</span>
                    <span class="context-pill">
                        <span class="pill-icon" aria-hidden="true">📌</span>
                        <span class="pill-text">{{ customerName }}</span>
                    </span>
                </div>

                <!-- Input -->
                <div class="assistant-input-row">
                    <textarea
                        ref="inputEl"
                        class="assistant-input"
                        v-model="question"
                        @input="autoGrow"
                        :disabled="loading || creditsExhausted"
                        :placeholder="placeholderText"
                        rows="1"
                        maxlength="500"></textarea>
                    <button
                        type="button"
                        class="assistant-send"
                        @click="ask"
                        :disabled="loading || creditsExhausted || !question || question.trim().length < 2"
                        :aria-label="loading ? 'Cevap üretiliyor' : 'Sor'">
                        <span v-if="!loading">↑</span>
                        <span v-else class="spinner" aria-hidden="true"></span>
                    </button>
                </div>
            </div>

            <!-- Beta uyarısı -->
            <div class="beta-disclaimer" role="note">
                <span class="beta-disclaimer-icon" aria-hidden="true">⚠️</span>
                <span>{{ $t('AssistantPanel.VegaBetaDisclaimer') }}</span>
            </div>
        </div>
    </div>
</template>

<script>
    const SESSION_IDLE_MS = 30 * 60 * 1000;
    const MAX_HISTORY_TURNS = 20;
    const INVITE_DELAY_MS = 2000;
    const INVITE_MIN_CHARS = 160;

    function newGuid() {
        if (window.crypto && typeof window.crypto.randomUUID === 'function') {
            return window.crypto.randomUUID();
        }
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }

    import axios from '../../services/index';
    import AppointmentConfirmCard from './AppointmentConfirmCard.vue';
    import AppointmentBatchCard from './AppointmentBatchCard.vue';
    import NoteConfirmCard from './NoteConfirmCard.vue';
    import AssistantQuickQuestionsEditor from './AssistantQuickQuestionsEditor.vue';
    import AiAssistantQuickQuestionService from '../../services/aiAssistantQuickQuestion';

    /**
     * Tool → durum metni anahtarı. Tümü değil, kullanıcıya ANLAMLI gelen ve gözle görülür
     * süre alan çağrılar; kalanlar genel metne düşer.
     */
    const TOOL_STATUS_KEYS = {
        // Okuma / analiz
        GetProfile: 'AiStatusProfile',
        GetClinicalProfile: 'AiStatusClinical',
        GetSalesSummary: 'AiStatusSales',
        GetSalesOnDate: 'AiStatusSales',
        GetSalesServiceHistory: 'AiStatusSales',
        GetSalesNotes: 'AiStatusSales',
        GetFinancialSummary: 'AiStatusAccounting',
        GetAppointmentSummary: 'AiStatusAppointments',
        GetAppointments: 'AiStatusAppointments',
        GetCompletedServiceUsage: 'AiStatusPerformedActivity',
        GetPerformedActivityUsage: 'AiStatusPerformedActivity',
        GetCustomerPackages: 'AiStatusPackages',
        GetTreatmentCards: 'AiStatusTreatmentCards',
        GetCustomerPhotoMetadata: 'AiStatusPhotos',
        GetProposesSummary: 'AiStatusProposes',
        GetProposesOnDate: 'AiStatusProposes',
        GetProposalNotes: 'AiStatusProposes',
        GetRecentNotes: 'AiStatusNotes',
        GetWhatsappMessages: 'AiStatusWhatsapp',
        GetSentSms: 'AiStatusSms',
        GetCustomerDiagnoses: 'AiStatusDiagnoses',
        GetCustomerArrivals: 'AiStatusArrivals',
        GetDietFollowUp: 'AiStatusDiet',
        GetObesityFollowUp: 'AiStatusObesity',
        GetCustomerDiets: 'AiStatusDiets',
        GetToothTreatments: 'AiStatusTooth',
        GetMyDayAppointments: 'AiStatusMyDay',
        GetMyNextAppointment: 'AiStatusMyDay',
        // Katalog / arama
        ListServices: 'AiStatusServices',
        GetServiceGroups: 'AiStatusServices',
        ListRooms: 'AiStatusRooms',
        LookupStaff: 'AiStatusStaff',
        FindDayAvailability: 'AiStatusAvailability',
        // Taslak üretimi
        ProposeAppointment: 'AiStatusDraftingAppointment',
        ProposeRecurringAppointments: 'AiStatusDraftingAppointment',
        ProposePackageAppointment: 'AiStatusDraftingAppointment',
        ProposeRecurringPackageAppointments: 'AiStatusDraftingAppointment',
        ProposeNote: 'AiStatusDraftingNote',
        ResolveTreatmentCardContext: 'AiStatusTreatmentCards',
        ProposeTreatmentCard: 'AiStatusDraftingTreatmentCard'
    };

    export default {
        name: 'AssistantPanel',
        components: {
            AppointmentConfirmCard,
            AppointmentBatchCard,
            NoteConfirmCard,
            AssistantQuickQuestionsEditor
        },
        props: {
            customerId: { type: String, default: '' },
            customerName: { type: String, default: '' },
            scope: { type: String, default: 'customer' }
        },
        data() {
            return {
                question: '',
                loading: false,
                errorMsg: '',
                successMsg: '',
                creditsExhausted: false,
                appointmentDrafts: [],
                noteDrafts: [],
                statusText: '',
                lastAiCallLogId: null,
                activeFlow: null,
                sessionId: null,
                lastMessageAt: null,
                messages: [],
                copiedIndex: -1,
                showHistory: false,
                historyLoading: false,
                historyError: '',
                historyList: [],
                showQuickQuestionEditor: false,
                quickQuestionStates: {},
                quickQuestionMaxCount: 6,
                quickQuestionMaxLength: 160
            };
        },
        mounted() {
            this.loadQuickQuestions();
            // override.js (assets/js/override.js) document seviyesinde *capture phase*'de
            // textarea Enter'larında stopPropagation çağırıyor → textarea'ya attach edilen
            // keydown handler'ları hiç fire etmiyor. Window-level capture, document'tan
            // ÖNCE çalışır; burada Enter'ı yakalayıp stopImmediatePropagation ile override'ı
            // bypass ediyoruz. Sadece kendi textarea'mızı hedefliyoruz.
            this._onWindowKeydown = (e) => {
                if (e.target !== this.$refs.inputEl) return;
                if (e.key !== 'Enter' && e.keyCode !== 13) return;
                if (e.shiftKey || e.ctrlKey || e.altKey || e.metaKey) return;
                if (e.isComposing || e.keyCode === 229) return;
                e.preventDefault();
                e.stopImmediatePropagation();
                this.ask();
            };
            window.addEventListener('keydown', this._onWindowKeydown, true);
        },
        beforeUnmount() {
            if (this._onWindowKeydown) {
                window.removeEventListener('keydown', this._onWindowKeydown, true);
                this._onWindowKeydown = null;
            }
            this.clearInviteTimer();
        },
        watch: {
            customerId: {
                immediate: true,
                handler() { this.resetState(); }
            },
            scope() {
                this.resetState();
                this.showQuickQuestionEditor = false;
                this.loadQuickQuestions(this.scope);
            },
            messages: {
                deep: true,
                handler() { this.scrollToBottomSoon(); }
            },
            loading() { this.scrollToBottomSoon(); },
            appointmentDrafts: { deep: true, handler() { this.scrollToBottomSoon(); } },
            noteDrafts: { deep: true, handler() { this.scrollToBottomSoon(); } }
        },
        computed: {
            // Turun konusu hazır mı? Boşsa istek gönderilmez; sunucu da aynı kuralı uygular
            // (scope'un gerektirdiği kimlik yoksa istek geçersizdir).
            hasSubject() {
                return !!this.customerId;
            },
            // Vega klinik odaklıdır; CRM'e özgü "kaybetme riski" sorusu yerine son muayene sorulur.
            defaultCustomerSuggestions() {
                return [
                    this.$t('AssistantPanel.CustomerSuggestion1'),
                    this.$t('AssistantPanel.CustomerSuggestion2'),
                    this.$t('AssistantPanel.CustomerSuggestion3'),
                    this.$t('AssistantPanel.VegaCustomerSuggestion4')
                ];
            },
            supportsQuickQuestions() {
                return this.scope === 'customer';
            },
            currentQuickQuestionState() {
                return this.quickQuestionStates[this.scope] || {
                    loaded: false,
                    questions: [],
                    isCustomized: false
                };
            },
            hasPersonalQuickQuestions() {
                return this.currentQuickQuestionState.isCustomized
                    && this.currentQuickQuestionState.questions.length > 0;
            },
            quickQuestionContextLabel() {
                return this.$t('Customer');
            },
            activeSuggestions() {
                return this.hasPersonalQuickQuestions
                    ? this.currentQuickQuestionState.questions
                    : this.defaultCustomerSuggestions;
            },
            isEmpty() {
                return this.messages.length === 0 && !this.loading && !this.errorMsg
                    && !this.successMsg && !this.appointmentDrafts.length
                    && !this.noteDrafts.length
                    && !this.creditsExhausted;
            },
            placeholderText() {
                if (this.creditsExhausted) return this.$t('AssistantPanel.CreditsInputDisabled');
                return this.loading
                    ? this.$t('AssistantPanel.Generating')
                    : this.$t('AssistantPanel.InputPlaceholder');
            },
            lastAssistantIndex() {
                for (let i = this.messages.length - 1; i >= 0; i--) {
                    if (this.messages[i].role === 'assistant') return i;
                }
                return -1;
            },
            feedbackReasons() {
                return [
                    this.$t('AssistantPanel.ReasonMissing'),
                    this.$t('AssistantPanel.ReasonWrong'),
                    this.$t('AssistantPanel.ReasonIrrelevant'),
                    this.$t('Other')
                ];
            }
        },
        methods: {
            // Parent (FAB) bu metodu çağırarak panel'i sıfırlar (↻ butonu).
            reset() {
                this.resetState();
                this.question = '';
            },
            resetState() {
                if (!this.hasSubject) return;
                this.clearInviteTimer();
                this.sessionId = newGuid();
                this.lastMessageAt = Date.now();
                this.messages = [];
                this.errorMsg = '';
                this.successMsg = '';
                this.creditsExhausted = false;
                this.appointmentDrafts = [];
                this.noteDrafts = [];
                this.statusText = '';
                this.lastAiCallLogId = null;
                this.activeFlow = null;
                this.showHistory = false;
                this.showQuickQuestionEditor = false;
            },
            ensureFreshSession() {
                const now = Date.now();
                if (!this.sessionId || !this.lastMessageAt
                    || (now - this.lastMessageAt) > SESSION_IDLE_MS) {
                    this.sessionId = newGuid();
                    this.messages = [];
                    this.activeFlow = null;
                }
                this.lastMessageAt = now;
            },
            askSuggestion(s) {
                this.question = s;
                this.ask();
            },
            async loadQuickQuestions(scope = this.scope, force = false) {
                if (scope !== 'customer') return;
                const current = this.quickQuestionStates[scope];
                if (current && current.loaded && !force) return;
                try {
                    const result = await AiAssistantQuickQuestionService.get(scope);
                    this.setQuickQuestionState(scope, result);
                } catch {
                    // Yarım rollout veya geçici ağ hatasında asistanın boş duruma düşmesini
                    // önlemek için mevcut yerelleştirilmiş sistem soruları gösterilmeye devam eder.
                    this.quickQuestionStates[scope] = {
                        loaded: true,
                        questions: [],
                        isCustomized: false
                    };
                }
            },
            async openQuickQuestionEditor() {
                this.showHistory = false;
                const requestedScope = this.scope;
                if (!this.currentQuickQuestionState.loaded) {
                    await this.loadQuickQuestions(requestedScope, true);
                }
                if (requestedScope === this.scope) this.showQuickQuestionEditor = true;
            },
            setQuickQuestionState(scope, result) {
                const questions = result && Array.isArray(result.questions) ? result.questions : [];
                this.quickQuestionStates[scope] = {
                    loaded: true,
                    questions,
                    isCustomized: !!(result && result.isCustomized && questions.length)
                };
                this.quickQuestionMaxCount = Number(result && result.maxCount) || this.quickQuestionMaxCount;
                this.quickQuestionMaxLength = Number(result && result.maxLength) || this.quickQuestionMaxLength;
            },
            onQuickQuestionsSaved(result) {
                this.setQuickQuestionState(this.scope, result);
                this.showQuickQuestionEditor = false;
                if (this.$toastr && this.$toastr.success) {
                    this.$toastr.success(this.$t('AssistantPanel.QuickQuestionsSaved'));
                }
            },
            onQuickQuestionsReset(result) {
                this.setQuickQuestionState(this.scope, result);
                this.showQuickQuestionEditor = false;
                if (this.$toastr && this.$toastr.success) {
                    this.$toastr.success(this.$t('AssistantPanel.QuickQuestionsReset'));
                }
            },
            // Modelin ürettiği sade Markdown'ı güvenli HTML'e çevirir.
            // Önce HTML escape edilir; sonra yalnızca bilinen biçimler (kalın,
            // italik, kod, liste) eklenir. Çıktı ayrıca v-dompurify-html ile sanitize edilir.
            renderMarkdown(raw) {
                if (!raw) return '';
                let t = String(raw)
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;');

                // Inline: **kalın**, __kalın__, *italik*, _italik_, `kod`
                t = t.replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>');
                t = t.replace(/__([^_\n]+)__/g, '<strong>$1</strong>');
                t = t.replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, '$1<em>$2</em>');
                t = t.replace(/`([^`\n]+)`/g, '<code>$1</code>');

                // Blok: satır satır gez, liste ve paragrafları grupla
                const lines = t.split(/\r?\n/);
                let html = '';
                let inList = false;
                let para = [];
                const flushPara = () => {
                    if (para.length) { html += '<p>' + para.join('<br>') + '</p>'; para = []; }
                };
                const closeList = () => { if (inList) { html += '</ul>'; inList = false; } };
                for (let line of lines) {
                    const bullet = line.match(/^\s*(?:[-*•])\s+(.*)$/);
                    if (bullet) {
                        flushPara();
                        if (!inList) { html += '<ul>'; inList = true; }
                        html += '<li>' + bullet[1] + '</li>';
                        continue;
                    }
                    closeList();
                    if (line.trim() === '') { flushPara(); continue; }
                    para.push(line.replace(/^\s*#{1,6}\s+/, '')); // başlık # işaretlerini at
                }
                flushPara();
                closeList();
                return html;
            },
            scrollToBottomSoon(force) {
                const el = this.$refs.messagesArea;
                const stick = force || !el
                    || (el.scrollHeight - el.scrollTop - el.clientHeight) <= 140;
                if (!stick) return;
                this.$nextTick(() => {
                    const e = this.$refs.messagesArea;
                    if (e) e.scrollTop = e.scrollHeight;
                });
            },
            /**
             * Tool adı → kullanıcıya gösterilecek durum metni.
             *
             * Backend phase + opsiyonel tool ADINI gönderir; görünen metin burada locale'den gelir
             * (i18n dışına metin gömülmez). Bilinmeyen ad → genel metin: yeni bir tool
             * eklendiğinde durum satırı boş kalmaz, yalnız daha az spesifik olur.
             */
            toolStatusText(tool) {
                const key = TOOL_STATUS_KEYS[tool];
                return this.$t(key || 'AiStatusWorking');
            },
            /**
             * Yeni status frame'i phase + opsiyonel tool taşır. Eski backend/yarım rollout
             * sırasında gelen yalnız { tool } gövdeleri de aynı eşlemeyle çalışmaya devam eder.
             */
            statusPayloadText(payload) {
                const status = payload && typeof payload === 'object' ? payload : {};
                const phase = String(status.phase || '').trim().toLowerCase();
                const tool = status.tool || status.capability;

                if (phase === 'planning') return this.$t('AiStatusPlanning');
                if (phase === 'composing') return this.$t('AiStatusComposing');
                if (phase === 'reading') {
                    return tool ? this.toolStatusText(tool) : this.$t('AiStatusReading');
                }

                // Geriye dönük uyumluluk: phase içermeyen mevcut { tool } sözleşmesi.
                return this.toolStatusText(tool);
            },
            isInviteSlot(msg, i) {
                return !!(msg.aiCallLogId && msg.feedback
                    && i === this.lastAssistantIndex
                    && msg.feedback.rating === null
                    && !msg.feedback.chipsOpen
                    && !this.appointmentDrafts.length
                    && !this.noteDrafts.length
                    && (msg.content || '').length >= INVITE_MIN_CHARS);
            },
            scheduleInvite(msg) {
                if (this._inviteTimer) clearTimeout(this._inviteTimer);
                this._inviteTimer = setTimeout(() => {
                    this._inviteTimer = null;
                    if (msg && msg.feedback) msg.feedback.inviteReady = true;
                }, INVITE_DELAY_MS);
            },
            clearInviteTimer() {
                if (this._inviteTimer) {
                    clearTimeout(this._inviteTimer);
                    this._inviteTimer = null;
                }
            },
            async copyMessage(content, index) {
                try {
                    await navigator.clipboard.writeText(content);
                    this.copiedIndex = index;
                    if (this.$toastr && this.$toastr.success) {
                        this.$toastr.success(this.$t('Copied'));
                    }
                    setTimeout(() => {
                        if (this.copiedIndex === index) this.copiedIndex = -1;
                    }, 1500);
                } catch {
                    // sessizce geç
                }
            },
            // Geçmiş uçları route'un konu eksenine göre filtreler.
            historyParams() {
                return { customerId: this.customerId };
            },
            toggleHistory() {
                if (this.showHistory) { this.showHistory = false; return; }
                this.showHistory = true;
                this.fetchHistory();
            },
            async fetchHistory() {
                if (!this.hasSubject) return;
                this.historyLoading = true;
                this.historyError = '';
                this.historyList = [];
                try {
                    const res = await axios.get('/api/AiAssistant/history', {
                        params: this.historyParams()
                    });
                    this.historyList = (res && res.data && res.data.conversations) || [];
                } catch {
                    this.historyError = 'Geçmiş yüklenemedi.';
                } finally {
                    this.historyLoading = false;
                }
            },
            async openConversation(sessionId) {
                if (!sessionId) return;
                this.historyLoading = true;
                try {
                    const res = await axios.get('/api/AiAssistant/history/' + sessionId, {
                        params: this.historyParams()
                    });
                    const msgs = (res && res.data && res.data.messages) || [];
                    this.messages = msgs.map(m => ({
                        role: m.role,
                        content: m.content,
                        aiCallLogId: m.aiCallLogId || null,
                        feedback: {
                            rating: m.feedbackRating || null,
                            comment: '',
                            reason: null,
                            commentOpen: false,
                            chipsOpen: false,
                            saving: false,
                            inviteReady: true
                        }
                    }));
                    this.sessionId = sessionId;
                    this.lastMessageAt = Date.now();
                    this.errorMsg = '';
                    this.successMsg = '';
                    this.appointmentDrafts = [];
                    this.noteDrafts = [];
                    this.statusText = '';
                    this.activeFlow = null;
                    this.showHistory = false;
                    this.scrollToBottomSoon(true);
                } catch {
                    this.historyError = 'Sohbet yüklenemedi.';
                } finally {
                    this.historyLoading = false;
                }
            },
            formatHistoryDate(iso) {
                if (!iso) return '';
                try {
                    return new Date(iso).toLocaleString('tr-TR', {
                        day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
                    });
                } catch {
                    return '';
                }
            },
            toggleLike(msg) {
                if (!msg.aiCallLogId || msg.feedback.saving) return;
                if (msg.feedback.rating === 'up') {
                    this.applyFeedback(msg, null);
                } else {
                    msg.feedback.commentOpen = false;
                    msg.feedback.chipsOpen = false;
                    msg.feedback.reason = null;
                    this.applyFeedback(msg, 'up');
                }
            },
            toggleDislike(msg) {
                if (!msg.aiCallLogId || msg.feedback.saving) return;
                if (msg.feedback.rating === 'down') {
                    msg.feedback.chipsOpen = false;
                    msg.feedback.commentOpen = false;
                    msg.feedback.reason = null;
                    this.applyFeedback(msg, null);
                } else {
                    msg.feedback.reason = null;
                    msg.feedback.commentOpen = false;
                    msg.feedback.chipsOpen = true;
                    this.applyFeedback(msg, 'down');
                }
            },
            selectReason(msg, reason) {
                if (!msg.aiCallLogId || msg.feedback.saving) return;
                msg.feedback.reason = reason;
                if (reason === this.$t('Other')) {
                    msg.feedback.commentOpen = true;
                    return;
                }
                msg.feedback.commentOpen = false;
                this.applyFeedback(msg, 'down', reason, () => {
                    msg.feedback.chipsOpen = false;
                });
            },
            submitComment(msg) {
                const text = (msg.feedback.comment || '').trim();
                this.applyFeedback(msg, 'down', text || this.$t('Other'), () => {
                    msg.feedback.commentOpen = false;
                    msg.feedback.chipsOpen = false;
                });
            },
            cancelComment(msg) {
                msg.feedback.commentOpen = false;
                if (msg.feedback.reason === this.$t('Other')) msg.feedback.reason = null;
            },
            applyFeedback(msg, rating, comment, onSuccess) {
                if (!msg.aiCallLogId) return;
                const fb = msg.feedback;
                const prevRating = fb.rating;
                fb.rating = rating;
                fb.saving = true;
                axios.post('/api/AiAssistant/feedback', {
                    aiCallLogId: msg.aiCallLogId,
                    rating: rating === null ? 'none' : rating,
                    comment: rating === 'down' ? (comment || '') : null
                }).then(() => {
                    if (typeof onSuccess === 'function') onSuccess();
                }).catch(() => {
                    fb.rating = prevRating;
                    fb.chipsOpen = false;
                    if (this.$toastr && this.$toastr.error) {
                        this.$toastr.error(this.$t('AssistantPanel.FeedbackSaveError'));
                    }
                }).finally(() => {
                    fb.saving = false;
                });
            },
            autoGrow() {
                this.$nextTick(() => {
                    const el = this.$refs.inputEl;
                    if (!el) return;
                    el.style.height = 'auto';
                    const max = 96;
                    el.style.height = Math.min(el.scrollHeight, max) + 'px';
                });
            },
            async ask() {
                const q = (this.question || '').trim();
                if (!q || q.length < 2 || this.loading) return;
                if (!this.hasSubject) return;

                this.ensureFreshSession();
                this.clearInviteTimer();

                this.errorMsg = '';
                this.successMsg = '';
                this.appointmentDrafts = [];
                this.noteDrafts = [];
                this.statusText = '';
                this.question = '';
                this.autoGrow(); // textarea'yı tek satıra reset et

                const history = this.messages.slice(-MAX_HISTORY_TURNS)
                    .map(m => ({ role: m.role, content: m.content }));

                // User mesajını HEMEN thread'e ekle
                this.messages.push({ role: 'user', content: q });
                this.loading = true;
                this.scrollToBottomSoon(true);

                // ── Streaming (SSE over axios onDownloadProgress) ──
                let assistantMsg = null;   // canlı asistan balonu (ilk delta'da oluşturulur)
                let processed = 0;         // responseText'te işlenen karakter
                let buffer = '';           // yarım kalan SSE frame
                let gotError = null;

                const appendDelta = (text) => {
                    if (!text) return;
                    if (!assistantMsg) {
                        this.loading = false;
                        assistantMsg = {
                            role: 'assistant',
                            content: '',
                            aiCallLogId: null,
                            feedback: { rating: null, comment: '', reason: null, commentOpen: false, chipsOpen: false, saving: false, inviteReady: false }
                        };
                        this.messages.push(assistantMsg);
                    }
                    assistantMsg.content += text;
                };

                const handleFrame = (frame) => {
                    let event = 'message', data = '';
                    for (const line of frame.split('\n')) {
                        if (line.indexOf('event:') === 0) event = line.slice(6).trim();
                        else if (line.indexOf('data:') === 0) data += line.slice(5).trim();
                    }
                    if (!data) return;
                    let obj;
                    try { obj = JSON.parse(data); } catch (e) {
                        // Sessizce yutulursa 'done' frame'i kaybolduğunda onay kartı hiç
                        // görünmez ve geriye HİÇBİR iz kalmaz (canlı şikâyet: "kart gelmedi",
                        // sunucu log'unda taslaklar canCreate=true olmasına rağmen).
                        console.error('[AssistantPanel] SSE frame parse edilemedi', event, data);
                        return;
                    }
                    if (event === 'delta') {
                        // İlk metin geldiği an tool döngüsü bitmiştir → durum metnini kaldır.
                        this.statusText = '';
                        appendDelta(obj.text);
                    }
                    else if (event === 'status') this.statusText = this.statusPayloadText(obj);
                    else if (event === 'done') {
                        this.statusText = '';
                        this.appointmentDrafts = obj.appointmentDrafts || [];
                        this.noteDrafts = obj.noteDrafts || [];
                        // Normal mesajlarda kullanıcı yukarı kaydırdıysa konumunu koruyoruz.
                        // Ancak bir onay kartı, kullanıcının başlattığı action turunun zorunlu
                        // devamıdır. Uzun asistan cevabı 140px stick eşiğini aşmış olsa bile kart
                        // görünür alana gelmeli; watcher'ın koşullu kaydırmasına bırakma.
                        if (this.appointmentDrafts.length || this.noteDrafts.length) {
                            this.scrollToBottomSoon(true);
                        }
                        this.lastAiCallLogId = obj.aiCallLogId || null;
                        this.activeFlow = obj.activeFlow || null;
                        if (assistantMsg && obj.aiCallLogId) {
                            assistantMsg.aiCallLogId = obj.aiCallLogId;
                            this.scheduleInvite(assistantMsg);
                        }
                    }
                    else if (event === 'error') gotError = obj.errorCode || 'E-AI-ERROR';
                };

                // responseText büyür; son indeksten itibaren tam frame'leri ("\n\n") işle.
                const feed = (fullText) => {
                    if (typeof fullText !== 'string' || fullText.length <= processed) return;
                    buffer += fullText.substring(processed);
                    processed = fullText.length;
                    let idx;
                    while ((idx = buffer.indexOf('\n\n')) !== -1) {
                        const frame = buffer.slice(0, idx);
                        buffer = buffer.slice(idx + 2);
                        if (frame.trim()) handleFrame(frame);
                    }
                };

                try {
                    // Kimlik alanı scope'a göre TEK gönderilir: boş string Guid'e çözülemez,
                    // gereksiz alan da sunucuda yanlış bağlam kurar.
                    const payload = {
                        question: q,
                        scope: this.scope,
                        customerId: this.customerId,
                        sessionId: this.sessionId,
                        history: history,
                        activeFlow: this.activeFlow
                    };

                    const res = await axios.post('/api/AiAssistant/ask-stream', payload, {
                        headers: { Accept: 'text/event-stream' },
                        responseType: 'text',
                        onDownloadProgress: (e) => {
                            const xhr = (e && e.event && e.event.target) || (e && e.target);
                            if (xhr && typeof xhr.responseText === 'string') feed(xhr.responseText);
                        }
                    });

                    // Son baytları garanti işle (bazı tarayıcılarda son chunk progress'e düşmeyebilir).
                    feed(typeof res.data === 'string' ? res.data : '');

                    if (gotError) {
                        this.handleAiError(gotError);
                        if (assistantMsg && !assistantMsg.content) this.messages.pop();
                    } else if (assistantMsg && assistantMsg.content) {
                        this.lastMessageAt = Date.now();
                        if (this.messages.length > MAX_HISTORY_TURNS * 2) {
                            this.messages = this.messages.slice(-MAX_HISTORY_TURNS * 2);
                        }
                    } else {
                        this.errorMsg = this.$t('AssistantPanel.NoResponse');
                    }
                } catch (err) {
                    const code = err && err.response && err.response.data && err.response.data.errorCode;
                    this.handleAiError(code);
                    if (assistantMsg && !assistantMsg.content) this.messages.pop();
                } finally {
                    this.loading = false;
                }
            },
            handleAiError(code) {
                if (code === 'E-AI-CREDITS') {
                    this.creditsExhausted = true;
                    this.errorMsg = '';
                    return;
                }
                // Kişisel kullanım kotası (kliniğin kredisi değil) — geçici, kendiliğinden açılır.
                if (code === 'E-AI-QUOTA') {
                    this.errorMsg = this.$t('AssistantPanel.QuotaExceeded');
                    return;
                }
                // Yatay yetki: bu danışan bu personele ait değil (ShowOnlySelfCustomers).
                if (code === 'E-AI-CUSTOMER-FORBIDDEN') {
                    this.errorMsg = this.$t('AssistantPanel.CustomerForbidden');
                    return;
                }
                this.errorMsg = this.$t('AssistantPanel.GenericError');
            },
            removeDraft(d) {
                const i = this.appointmentDrafts.indexOf(d);
                if (i !== -1) this.appointmentDrafts.splice(i, 1);
            },
            // Tek randevu kartı kendi /Appointment/New çağrısını yapar; başarı → kapat.
            onAppointmentConfirmed(d, result) {
                this.recordActionOutcome(d, 'confirmed', result && (result.id || result.Id));
                this.removeDraft(d);
                if (!this.appointmentDrafts.length) {
                    this.resetState();
                    this.successMsg = 'Randevu oluşturuldu.';
                }
            },
            onAppointmentDismissed(d) {
                this.recordActionOutcome(d, 'cancelled');
                this.removeDraft(d);
            },
            onAppointmentEdit() {
                this.appointmentDrafts.forEach(d => this.recordActionOutcome(d, 'cancelled'));
                this.appointmentDrafts = [];
                this.question = 'Randevuyu düzenle: ';
                this.$nextTick(() => {
                    const input = this.$el.querySelector('.assistant-input');
                    if (input) input.focus();
                });
            },
            onBatchDraftConfirmed(payload) {
                if (!payload || !payload.draft) return;
                const result = payload.result;
                this.recordActionOutcome(
                    payload.draft,
                    'confirmed',
                    result && (result.id || result.Id));
                // Kısmi toplu başarıda oluşturulan taslağı hemen listeden çıkar. Böylece
                // kullanıcı kalan hatalı taslakları yeniden denerken başarılı randevu ikinci kez
                // oluşturulmaz; iptal edilirse de yalnız gerçekten bekleyenler Cancelled olur.
                this.removeDraft(payload.draft);
            },
            // Toplu kart: tüm uygun randevular oluşturuldu.
            onBatchConfirmed(payload) {
                const ok = (payload && payload.ok) || this.appointmentDrafts.filter(d => d && d.canCreate).length;
                this.appointmentDrafts = [];
                this.resetState();
                this.successMsg = ok + ' randevu oluşturuldu.';
            },
            onBatchDismissed() {
                this.appointmentDrafts.forEach(d => this.recordActionOutcome(d, 'cancelled'));
                this.appointmentDrafts = [];
            },
            removeNoteDraft(d) {
                const i = this.noteDrafts.indexOf(d);
                if (i !== -1) this.noteDrafts.splice(i, 1);
            },
            // Not kartı kendi /CustomerNote/InsertMany çağrısını yapar; başarı → kapat + listeleri tazele.
            onNoteConfirmed(d, result) {
                this.recordActionOutcome(d, 'confirmed', result && (result.id || result.Id));
                this.removeNoteDraft(d);
                if (!this.noteDrafts.length) {
                    this.resetState();
                    this.successMsg = this.$t('AssistantPanel.NoteCreated');
                }
            },
            onNoteDismissed(d) {
                this.recordActionOutcome(d, 'cancelled');
                this.removeNoteDraft(d);
            },
            onNoteEdit() {
                this.noteDrafts.forEach(d => this.recordActionOutcome(d, 'cancelled'));
                this.noteDrafts = [];
                this.question = 'Notu düzenle: ';
                this.$nextTick(() => {
                    const input = this.$el.querySelector('.assistant-input');
                    if (input) input.focus();
                });
            },
            async recordActionOutcome(draft, outcome, targetId) {
                if (!draft || !draft.actionDraftId) return;
                try {
                    await axios.post('/api/AiAssistant/action-outcome', {
                        actionDraftId: draft.actionDraftId,
                        outcome,
                        targetId: targetId || null
                    });
                } catch (err) {
                    // CRM kaydı başarıyla oluştuysa audit ağ hatası kullanıcı akışını geri
                    // çevirmemeli. Saatlik telemetry pending farkıyla eksik sonucu görünür kılar.
                    if (process.env.NODE_ENV !== 'production') console.warn('AI action audit failed', err);
                }
            }
        }
    };
</script>

<style scoped>
    /* ═══════════════════════════════════════════════════════════
       AssistantPanel — Estesoft.App AssistantPanel.vue'nin Vega kopyası. Modern, ferah AI sohbeti (ChatGPT/Claude/Linear
       altyapısı). Brand: indigo→violet→pembe "rainbow" aksanı.
       Konuşma arka planı temiz; mesaj satırlarında karartma YOK.
       ═══════════════════════════════════════════════════════════ */

    .assistant-panel {
        /* Tek noktadan yönetilen, hafifçe akan AI rainbow degradesi */
        --ai-rainbow: linear-gradient(135deg,
            #6366f1 0%, #7c5cf0 28%, #a855f7 52%, #ec4899 78%, #6366f1 100%);
        --ai-indigo: #6366f1;
        --ai-violet: #8b5cf6;
        --ai-pink: #ec4899;

        display: flex;
        flex-direction: column;
        height: 100%;
        background: #ffffff;
        min-height: 0;
        position: relative;
    }

    /* ─── Messages area (scrollable conversation thread) ─── */
    .messages-area {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        padding: 16px 14px;
        display: flex;
        flex-direction: column;
        gap: 14px;
        scroll-behavior: smooth;
        /* Temiz, çok hafif dikey degrade — karartma yok */
        background:
            radial-gradient(120% 60% at 50% 0%, rgba(99,102,241,0.045) 0%, rgba(99,102,241,0) 60%),
            linear-gradient(180deg, #ffffff 0%, #fbfbfe 100%);
    }

    .messages-area::-webkit-scrollbar {
        width: 6px;
    }
    .messages-area::-webkit-scrollbar-thumb {
        background: #cbd5e1;
        border-radius: 3px;
    }
    .messages-area::-webkit-scrollbar-thumb:hover {
        background: #94a3b8;
    }

    /* ─── Message bubble (user / assistant) ───
       Satırların kendisi TAMAMEN şeffaf — eski "karartma" görünümü
       (gri kutu) burada kesin olarak nötrleniyor. */
    .message {
        display: flex;
        align-items: flex-start;
        gap: 9px;
        background: transparent !important;
        box-shadow: none !important;
        border: none !important;
        padding: 0;
        animation: msg-fade 0.25s ease-out;
    }
    .message-user {
        justify-content: flex-end;
    }
    .message-assistant {
        justify-content: flex-start;
    }

    /* Animasyonlu rainbow avatar — "bu bir AI" sinyali */
    .msg-avatar {
        flex-shrink: 0;
        width: 30px;
        height: 30px;
        margin-top: 16px; /* "Vega AI" etiketi hizası */
        border-radius: 50%;
        background: var(--ai-rainbow);
        background-size: 200% 200%;
        animation: ai-shimmer 7s ease infinite;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 8px rgba(124, 92, 240, 0.30);
    }
    .msg-avatar span { font-size: 14px; color: white; line-height: 1; }

    .msg-content {
        display: flex;
        flex-direction: column;
        gap: 4px;
        max-width: 80%;
        min-width: 0;
    }
    .message-user .msg-content {
        align-items: flex-end;
    }

    /* "Vega AI ✨" mini etiketi (asistan balonlarının üstünde) */
    .msg-name {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        font-size: var(--est-fs-meta);
        font-weight: 700;
        letter-spacing: 0.01em;
        background: var(--ai-rainbow);
        background-size: 200% 200%;
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
        color: transparent;
        animation: ai-shimmer 7s ease infinite;
        padding-left: 2px;
    }
    .msg-name-spark {
        -webkit-text-fill-color: initial;
        color: var(--ai-violet);
        font-size: 10px;
    }

    .msg-bubble {
        padding: 10px 13px;
        border-radius: 14px;
        font-size: var(--est-fs-body);
        line-height: 1.55;
        white-space: pre-wrap;
        word-wrap: break-word;
    }
    /* ─── Geri bildirim ayağı ─── */
    .msg-actions {
        align-self: stretch;
        margin-top: 2px;
    }

    .fb-compact {
        display: inline-flex;
        align-items: center;
        gap: 2px;
        flex-wrap: wrap;
    }

    .fb-icon-btn {
        border: none;
        background: transparent;
        color: #64748b;
        cursor: pointer;
        min-width: 32px;
        min-height: 32px;
        padding: 4px 7px;
        font-size: 14px;
        line-height: 1;
        border-radius: 8px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        transition: background 0.15s ease, color 0.15s ease,
                    box-shadow 0.15s ease, transform 0.1s ease;
    }
    .fb-icon-btn:hover:not(:disabled) { background: #f1f5f9; color: #475569; }
    .fb-icon-btn:active:not(:disabled) { transform: scale(0.92); }
    .fb-icon-btn:disabled { cursor: default; opacity: 0.55; }
    .fb-icon-btn:focus-visible {
        outline: 2px solid rgba(139, 92, 246, 0.5);
        outline-offset: 1px;
    }
    .fb-copy.is-copied { color: #10b981; }

    /* Emoji rengi ezilemez; aktiflik arka plan + ring ile verilir. */
    .fb-thumb.is-up   { background: #f1ecff; box-shadow: inset 0 0 0 1px #d8ccfb; }
    .fb-thumb.is-down { background: #fdeef1; box-shadow: inset 0 0 0 1px #f6cdd6; }
    .fb-sep {
        width: 1px;
        height: 16px;
        background: #eceaf6;
        margin: 0 4px;
        flex-shrink: 0;
    }

    .fb-invite {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px 10px;
        padding: 7px 8px 7px 13px;
        background: linear-gradient(180deg, #fbfaff 0%, #f6f3ff 100%);
        border: 1px solid #ece7fb;
        border-radius: 12px;
        animation: fb-fade 0.25s ease-out;
    }
    .fb-invite-q {
        flex: 1 1 auto;
        font-size: var(--est-fs-meta);
        font-weight: 500;
        color: #5b5470;
        line-height: 1.3;
    }
    .fb-invite-actions {
        display: inline-flex;
        align-items: center;
        gap: 6px;
    }
    .fb-choice {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        min-height: 34px;
        border: 1px solid #e4dff6;
        background: #ffffff;
        color: #4b4560;
        cursor: pointer;
        padding: 6px 14px;
        font-size: var(--est-fs-body);
        font-weight: 500;
        font-family: inherit;
        line-height: 1;
        border-radius: 999px;
        transition: border-color 0.15s ease, color 0.15s ease,
                    box-shadow 0.15s ease, transform 0.1s ease;
    }
    .fb-choice > span:first-child { font-size: 14px; }
    .fb-choice:hover:not(:disabled) {
        border-color: var(--ai-violet);
        color: #4338ca;
        box-shadow: 0 2px 8px rgba(124, 92, 240, 0.14);
    }
    .fb-choice:active:not(:disabled) { transform: scale(0.96); }
    .fb-choice:disabled { opacity: 0.6; cursor: default; }
    .fb-choice:focus-visible {
        outline: 2px solid rgba(139, 92, 246, 0.5);
        outline-offset: 1px;
    }

    .fb-reasons {
        align-self: stretch;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 6px;
        margin-top: 6px;
        animation: fb-fade 0.2s ease-out;
    }
    .fb-reasons-label { font-size: var(--est-fs-label); color: #64748b; margin-right: 2px; }
    .fb-reasons-label small { color: #94a3b8; }
    .fb-chip {
        min-height: 30px;
        border: 1px solid #e2e0ee;
        background: #fff;
        color: #475569;
        border-radius: 999px;
        padding: 6px 13px;
        font-size: var(--est-fs-meta);
        line-height: 1.15;
        font-family: inherit;
        cursor: pointer;
        transition: background 0.15s ease, border-color 0.15s ease,
                    color 0.15s ease, transform 0.1s ease;
    }
    .fb-chip:hover:not(:disabled) { background: #faf9ff; border-color: #c9bef7; color: #4338ca; }
    .fb-chip:active:not(:disabled) { transform: scale(0.96); }
    .fb-chip.is-selected { background: #f3f0ff; border-color: #b9a8f5; color: #4338ca; font-weight: 600; }
    .fb-chip:disabled { opacity: 0.6; cursor: default; }
    .fb-chip:focus-visible {
        outline: 2px solid rgba(139, 92, 246, 0.5);
        outline-offset: 1px;
    }

    @keyframes fb-fade {
        from { opacity: 0; transform: translateY(3px); }
        to   { opacity: 1; transform: translateY(0); }
    }
    @media (prefers-reduced-motion: reduce) {
        .fb-invite, .fb-reasons { animation: none; }
        .fb-icon-btn, .fb-choice, .fb-chip { transition: none; }
    }

    /* 👎 serbest metin yorum kutusu */
    .msg-feedback-box {
        align-self: stretch;
        margin-top: 6px;
        display: flex;
        flex-direction: column;
        gap: 6px;
    }
    .msg-feedback-input {
        width: 100%;
        resize: none;
        border: 1px solid #e2e0ee;
        border-radius: 10px;
        padding: 8px 10px;
        font-size: var(--est-fs-meta);
        line-height: 1.45;
        color: #1e1b34;
        background: #fff;
        outline: none;
        font-family: inherit;
        box-sizing: border-box;
    }
    .msg-feedback-input:focus { border-color: #b9a8f5; }
    .msg-feedback-actions {
        display: flex;
        justify-content: flex-end;
        gap: 6px;
    }
    .fb-btn {
        border: none;
        border-radius: 8px;
        padding: 5px 12px;
        font-size: var(--est-fs-meta);
        cursor: pointer;
        line-height: 1;
    }
    .fb-btn--ghost { background: transparent; color: #64748b; }
    .fb-btn--ghost:hover { background: #f1f5f9; color: #334155; }
    .fb-btn--primary {
        background: var(--ai-rainbow, #7c5cf0);
        background-size: 160% 160%;
        color: #fff;
    }
    .fb-btn--primary:disabled { opacity: 0.6; cursor: default; }
    .message-user .msg-bubble {
        background: var(--ai-rainbow);
        background-size: 160% 160%;
        color: white;
        border-bottom-right-radius: 5px;
        box-shadow: 0 4px 14px rgba(124, 92, 240, 0.28);
    }
    .message-assistant .msg-bubble {
        position: relative;
        background: #ffffff;
        border: 1px solid #eceaf6;
        color: #1e1b34;
        border-bottom-left-radius: 5px;
        box-shadow: 0 2px 10px rgba(99, 102, 241, 0.07),
                    0 1px 2px rgba(15, 23, 42, 0.04);
    }
    .msg-bubble--loading {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 13px 15px;
    }
    @keyframes msg-fade {
        from { opacity: 0; transform: translateY(6px); }
        to   { opacity: 1; transform: translateY(0); }
    }
    /* Avatar / etiket / send butonu için ortak rainbow akışı */
    @keyframes ai-shimmer {
        0%   { background-position: 0% 50%; }
        50%  { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
    }

    /* ─── "AI düşünüyor" animasyonu — avatar pulse + ikon wiggle ─── */
    .message-loading .msg-avatar {
        animation: avatar-thinking 1.6s ease-in-out infinite;
    }
    .message-loading .msg-avatar span {
        display: inline-block;
        animation: icon-wiggle 1.6s ease-in-out infinite;
    }
    @keyframes avatar-thinking {
        0%, 100% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(124, 92, 240, 0);
        }
        50% {
            transform: scale(1.08);
            box-shadow: 0 0 0 7px rgba(168, 85, 247, 0.18);
        }
    }
    @keyframes icon-wiggle {
        0%, 100% { transform: rotate(0deg) scale(1); }
        25%      { transform: rotate(-10deg) scale(1.06); }
        50%      { transform: rotate(0deg)   scale(1.12); }
        75%      { transform: rotate(10deg)  scale(1.06); }
    }

    /* ─── Context bar (Linear/Cmd+K stili) ─── */
    .context-bar {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 8px 8px 12px;
        border-bottom: 1px solid #f1f5f9;
    }
    .context-label {
        font-size: var(--est-fs-label);
        color: #94a3b8;
        font-weight: 500;
        white-space: nowrap;
    }
    .context-pill {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 3px 10px;
        background: #eef2ff;
        border: 1px solid #c7d2fe;
        border-radius: 6px;
        font-size: var(--est-fs-meta);
        color: #4338ca;
        font-weight: 500;
        min-width: 0;
        max-width: 240px;
    }
    .pill-icon { font-size: 12px; flex-shrink: 0; }
    .pill-text {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .history-overlay {
        position: absolute;
        inset: 0;
        z-index: 5;
        background: #ffffff;
        display: flex;
        flex-direction: column;
    }
    .history-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px 14px;
        border-bottom: 1px solid #f1f5f9;
        flex-shrink: 0;
    }
    .history-title {
        font-size: var(--est-fs-body);
        font-weight: 600;
        color: #1e1b34;
    }
    .history-back {
        width: 26px;
        height: 26px;
        border: none;
        background: transparent;
        color: #94a3b8;
        border-radius: 6px;
        cursor: pointer;
        font-size: 17px;
        line-height: 1;
    }
    .history-back:hover { background: #f1f5f9; color: #475569; }
    .history-body {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        padding: 10px 12px;
    }
    .history-state {
        padding: 24px 12px;
        text-align: center;
        color: #94a3b8;
        font-size: var(--est-fs-body);
    }
    .history-list {
        list-style: none;
        margin: 0;
        padding: 0;
        display: flex;
        flex-direction: column;
        gap: 6px;
    }
    .history-item {
        width: 100%;
        text-align: left;
        border: 1px solid #eceaf6;
        background: #fff;
        border-radius: 10px;
        padding: 10px 12px;
        cursor: pointer;
        display: flex;
        flex-direction: column;
        gap: 3px;
        transition: background 0.15s ease, border-color 0.15s ease;
    }
    .history-item:hover {
        background: #f7f5ff;
        border-color: #d9d2f7;
    }
    .history-item-title {
        font-size: var(--est-fs-body);
        color: #1e1b34;
        font-weight: 500;
        line-height: 1.35;
    }
    .history-item-meta {
        font-size: var(--est-fs-meta);
        color: #94a3b8;
    }

    /* ─── Empty state ─── */
    .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 16px;
        padding: 12px 8px 12px;
        flex: 1;
    }
    .empty-avatar {
        position: relative;
        width: 60px;
        height: 60px;
        border-radius: 50%;
        background: var(--ai-rainbow);
        background-size: 200% 200%;
        animation: ai-shimmer 7s ease infinite;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 26px;
        color: white;
        box-shadow: 0 6px 22px rgba(124, 92, 240, 0.32),
                    0 1px 3px rgba(15, 23, 42, 0.06);
    }
    /* Rainbow "halo" — empty state'te AI varlığını vurgular */
    .empty-avatar::after {
        content: '';
        position: absolute;
        inset: -6px;
        border-radius: 50%;
        background: var(--ai-rainbow);
        background-size: 200% 200%;
        animation: ai-shimmer 7s ease infinite;
        filter: blur(9px);
        opacity: 0.35;
        z-index: -1;
    }
    .empty-greeting {
        margin: 0;
        text-align: center;
        font-size: var(--est-fs-body);
        color: #475569;
        line-height: 1.5;
        max-width: 280px;
    }
    .empty-greeting strong {
        color: #0f172a;
        font-weight: 600;
    }
    .suggestion-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        margin-top: 4px;
        color: #64748b;
        font-size: var(--est-fs-meta);
        font-weight: 600;
    }
    .suggestion-head button {
        min-height: 30px;
        padding: 0 8px;
        border: none;
        border-radius: 8px;
        background: transparent;
        color: #6d4bd1;
        font-size: var(--est-fs-meta);
        cursor: pointer;
    }
    .suggestion-head button:hover { background: #f5f3ff; color: #4338ca; }
    .suggestion-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 8px;
        width: 100%;
        margin-top: 8px;
    }
    .suggestion-grid--headed { margin-top: -6px; }
    .suggestion-chip {
        font-size: var(--est-fs-meta);
        padding: 10px 12px;
        border: 1px solid #e2e8f0;
        background: #ffffff;
        color: #334155;
        border-radius: 10px;
        cursor: pointer;
        text-align: left;
        line-height: 1.35;
        transition: border-color 0.15s ease,
                    background 0.15s ease,
                    color 0.15s ease;
    }
    .suggestion-chip:hover {
        background: linear-gradient(180deg, #fbfaff 0%, #f5f3ff 100%);
        border-color: var(--ai-violet);
        color: #4338ca;
        box-shadow: 0 2px 8px rgba(124, 92, 240, 0.10);
    }
    @media (max-width: 420px) {
        .suggestion-grid { grid-template-columns: 1fr; }
    }

    /* ─── Loading dots (bubble içinde) ─── */
    .loading-dot {
        width: 6px; height: 6px; border-radius: 50%;
        animation: dot-bounce 1.4s ease-in-out infinite;
    }
    .loading-dot:nth-child(1) { background: var(--ai-indigo); }
    .loading-dot:nth-child(2) { background: var(--ai-violet); }
    .loading-dot:nth-child(3) { background: var(--ai-pink); }
    .loading-dot:nth-child(2) { animation-delay: 0.15s; }
    .loading-dot:nth-child(3) { animation-delay: 0.3s; }
    @keyframes dot-bounce {
        0%, 80%, 100% { transform: scale(0.5); opacity: 0.4; }
        40% { transform: scale(1); opacity: 1; }
    }

    /* ─── Success / Error ─── */
    .assistant-success, .assistant-error {
        display: inline-flex; align-items: center; gap: 8px;
        margin: 12px 16px;
        padding: 10px 14px;
        border-radius: 10px;
        font-size: var(--est-fs-meta);
        align-self: flex-start;
    }
    .assistant-success {
        background: #ecfdf5;
        border: 1px solid #a7f3d0;
        color: #047857;
    }
    .success-icon {
        width: 18px; height: 18px; border-radius: 50%;
        background: #10b981; color: white;
        display: inline-flex; align-items: center; justify-content: center;
        font-size: 11px; font-weight: 700;
    }
    .assistant-error {
        background: #fef2f2;
        border: 1px solid #fecaca;
        color: #b91c1c;
    }
    .error-icon {
        width: 18px; height: 18px; border-radius: 50%;
        background: #ef4444; color: white;
        display: inline-flex; align-items: center; justify-content: center;
        font-size: 11px; font-weight: 700;
    }

    /* ─── Kredi bitti bildirimi (hata değil, sakin marka kartı) ─── */
    .assistant-notice {
        display: flex;
        align-items: center;
        gap: 11px;
        margin: 12px 14px;
        padding: 14px 16px;
        border-radius: 12px;
        background: linear-gradient(180deg, #fbfaff 0%, #f4f0ff 100%);
        border: 1px solid #e4dcff;
        color: #4b4560;
        font-size: var(--est-fs-body);
        line-height: 1.5;
    }
    .notice-spark {
        flex-shrink: 0;
        width: 30px;
        height: 30px;
        border-radius: 50%;
        background: var(--ai-rainbow);
        background-size: 200% 200%;
        animation: ai-shimmer 7s ease infinite;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 15px;
        color: #fff;
        box-shadow: 0 2px 8px rgba(124, 92, 240, 0.30);
    }
    .notice-text { min-width: 0; }

    /* ─── Footer wrapper ─── */
    .panel-footer {
        margin-top: auto;
        padding: 12px 14px 14px;
        background: linear-gradient(180deg, #fbfbfe 0%, #ffffff 100%);
        border-top: 1px solid #f1f0f9;
    }

    /* Linear stilinde bordered tek kart — context bar + input alt alta */
    .footer-card {
        background: #ffffff;
        border: 1px solid #e2e8f0;
        border-radius: 12px;
        overflow: hidden;
        transition: border-color 0.15s ease, box-shadow 0.15s ease;
    }
    .footer-card:focus-within {
        border-color: var(--ai-violet);
        box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.12);
    }

    /* ─── Beta uyarısı (input kartının altında) ─── */
    .beta-disclaimer {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        margin-top: 8px;
        padding: 5px 10px;
        font-size: var(--est-fs-meta);
        font-weight: 500;
        line-height: 1.35;
        text-align: center;
        color: #b45309;
        background: #fffbeb;
        border: 1px solid #fde68a;
        border-radius: 8px;
    }
    .beta-disclaimer-icon {
        font-size: 12px;
        flex-shrink: 0;
    }

    /* ─── Input (kart içinde) ─── */
    .assistant-input-row {
        display: flex;
        align-items: flex-end; /* textarea büyüdüğünde send butonu altta hizalı kalsın */
        gap: 6px;
        padding: 6px 6px 6px 10px;
    }
    .assistant-input {
        flex: 1;
        padding: 6px 4px;
        font-size: var(--est-fs-body);
        font-family: inherit;
        line-height: 1.45;
        border: none;
        background: transparent;
        color: #0f172a;
        outline: none;
        resize: none;          /* drag handle gizle */
        overflow-y: auto;      /* 4 satır geçince scroll */
        max-height: 96px;
        min-height: 22px;
    }
    .assistant-input::placeholder { color: #94a3b8; }
    .assistant-input:disabled { opacity: 0.6; cursor: not-allowed; }

    .assistant-send {
        width: 34px;
        height: 34px;
        border: none;
        border-radius: 9px;
        background: var(--ai-rainbow);
        background-size: 200% 200%;
        animation: ai-shimmer 7s ease infinite;
        color: white;
        font-size: 16px;
        line-height: 1;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        box-shadow: 0 2px 8px rgba(124, 92, 240, 0.30);
        transition: transform 0.12s ease, box-shadow 0.15s ease, opacity 0.15s ease;
    }
    .assistant-send:hover:not(:disabled) {
        transform: scale(1.06);
        box-shadow: 0 4px 14px rgba(124, 92, 240, 0.42);
    }
    .assistant-send:disabled {
        opacity: 0.4;
        cursor: not-allowed;
        animation: none;
    }

    .spinner {
        width: 14px;
        height: 14px;
        border: 2px solid rgba(255, 255, 255, 0.4);
        border-top-color: white;
        border-radius: 50%;
        animation: spin 0.7s linear infinite;
    }
    @keyframes spin { to { transform: rotate(360deg); } }
</style>

<!-- Render edilen Markdown stilleri — v-dompurify-html ile üretilen içerik
     scoped attribute almadığı için bu blok SCOPED DEĞİL; .stella-md ile namespace'lendi. -->
<style>
    .msg-bubble.stella-md > :first-child { margin-top: 0; }
    .msg-bubble.stella-md > :last-child { margin-bottom: 0; }
    .msg-bubble.stella-md p {
        margin: 0 0 8px;
        line-height: 1.55;
    }
    .msg-bubble.stella-md strong {
        font-weight: 700;
        color: #0f1020;
    }
    .msg-bubble.stella-md em { font-style: italic; }
    .msg-bubble.stella-md ul {
        margin: 6px 0 8px;
        padding-left: 18px;
        list-style: none;
    }
    .msg-bubble.stella-md li {
        position: relative;
        margin: 3px 0;
        padding-left: 4px;
        line-height: 1.5;
    }
    .msg-bubble.stella-md li::before {
        content: '';
        position: absolute;
        left: -12px;
        top: 8px;
        width: 5px;
        height: 5px;
        border-radius: 50%;
        background: linear-gradient(135deg, #6366f1, #ec4899);
    }
    .msg-bubble.stella-md code {
        font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
        font-size: var(--est-fs-meta);
        background: #f3f1fb;
        border: 1px solid #e7e3f7;
        border-radius: 4px;
        padding: 1px 5px;
    }


    /* Tool döngüsü sırasında noktaların yanında akan durum metni. */
    .loading-status {
        margin-left: 8px;
        font-size: var(--est-fs-meta);
        color: #8a93a5;
        vertical-align: middle;
    }
</style>
